import type {
    ChatMessageAttachment,
    SendGeneralChatMessageAttachment,
    SendGeneralChatMessageRequest,
    SendRequestChatMessageAttachment,
    SendRequestChatMessageRequest,
} from "@albertine/types/openapi/concierge-v1";
import { config } from "@/config";
import { assertNever } from "../-utils/chatErrors";
import type { ChatOutboxDraft } from "./chatOutbox";

const GENERAL_CHAT_PATH = "/concierge/v1/members/me/general-chat";
const REQUEST_CHAT_PATH = "/concierge/v1/members/me/requests";

export const LOCAL_MEMBER_ID = "local-member";

export type ChatScope = { type: "general" } | { type: "request"; requestId: string };

export function buildSendGeneralChatMessageRequest(
    draft: ChatOutboxDraft,
): SendGeneralChatMessageRequest {
    const attachments: SendGeneralChatMessageAttachment[] = [
        ...catalogItemAttachments(draft.attachments),
        ...fileAttachments(draft),
    ];

    return {
        message: requireSendMessageContent(draft, attachments),
        messageHtml: draft.messageHtml || undefined,
        idempotencyKey: draft.sendingId,
        replyToMessageId: draft.reply?.id,
        attachments: attachments.length > 0 ? attachments : undefined,
    };
}

export function buildSendRequestChatMessageRequest(
    draft: ChatOutboxDraft,
): SendRequestChatMessageRequest {
    const attachments: SendRequestChatMessageAttachment[] = [
        ...catalogItemAttachments(draft.attachments),
        ...fileAttachments(draft),
        ...confirmedBookingAttachments(draft),
    ];

    return {
        message: requireSendMessageContent(draft, attachments),
        messageHtml: draft.messageHtml || undefined,
        idempotencyKey: draft.sendingId,
        replyToMessageId: draft.reply?.id,
        attachments: attachments.length > 0 ? attachments : undefined,
    };
}

export function getChatScopeKey(scope: ChatScope): string {
    switch (scope.type) {
        case "general":
            return "general";
        case "request":
            return `request:${scope.requestId}`;
        default:
            return assertNever(scope);
    }
}

export function getChatEventsUrl(scope: ChatScope): string {
    if (!config.apiServerBaseUrl) {
        throw new Error("VITE_API_SERVER_BASE_URL is required for API debug actions");
    }

    switch (scope.type) {
        case "general":
            return new URL(
                `${GENERAL_CHAT_PATH}/events`,
                config.apiServerBaseUrl,
            ).toString();
        case "request":
            return new URL(
                `${REQUEST_CHAT_PATH}/${encodeURIComponent(scope.requestId)}/chat/events`,
                config.apiServerBaseUrl,
            ).toString();
        default:
            return assertNever(scope);
    }
}

export function getChatEventsUrlSafe(scope: ChatScope): string | null {
    try {
        return getChatEventsUrl(scope);
    } catch {
        return null;
    }
}

function requireSendMessageContent(
    draft: ChatOutboxDraft,
    attachments: readonly unknown[],
): string {
    const message = draft.message.trim();
    if (message.length === 0 && attachments.length === 0) {
        throw new Error("Message cannot be empty");
    }

    return message;
}

function catalogItemAttachments(
    attachments: ChatMessageAttachment[],
): Extract<SendRequestChatMessageAttachment, { type: "catalog-item" }>[] {
    return attachments
        .filter((attachment) => attachment.type === "catalog-item")
        .map((attachment) => ({
            type: "catalog-item" as const,
            id: attachment.id,
        }));
}

function fileAttachments(
    draft: ChatOutboxDraft,
): Extract<SendRequestChatMessageAttachment, { type: "file" }>[] {
    return draft.uploads.flatMap((upload) =>
        upload.fileUploadId
            ? [{ type: "file" as const, fileUploadId: upload.fileUploadId }]
            : [],
    );
}

function confirmedBookingAttachments(
    draft: ChatOutboxDraft,
): Extract<SendRequestChatMessageAttachment, { type: "confirmed-booking" }>[] {
    return (draft.confirmedBookings ?? []).map((booking) => ({
        type: "confirmed-booking" as const,
        id: booking.id,
        optionId: booking.options?.[0]?.id,
    }));
}
